import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ETIQUETA_ROL_OPERADOR,
  ROLES_OPERADOR,
  formatearFechaHora,
  type EventoOperadorInfo,
  type OperadorInfo,
  type RolOperador,
} from '@cmms/shared';
import { api, ErrorApi } from '../../servicios/api.js';
import { useSesionOperador } from '../../servicios/sesion-operador.js';
import { Aviso, Cargando, Etiqueta, Vacio } from '../../componentes/piezas.js';

export function ListaOperadores(): JSX.Element {
  const { operador: yo, puede } = useSesionOperador();
  const cliente = useQueryClient();
  const [creando, setCreando] = useState(false);

  const { data: operadores, isLoading } = useQuery({
    queryKey: ['plataforma', 'operadores'],
    queryFn: () => api.get<OperadorInfo[]>('/plataforma/operadores'),
  });

  const crear = useMutation({
    mutationFn: (datos: unknown) => api.post<OperadorInfo>('/plataforma/operadores', datos),
    onSuccess: async () => {
      setCreando(false);
      await cliente.invalidateQueries({ queryKey: ['plataforma', 'operadores'] });
    },
  });

  const actualizar = useMutation({
    mutationFn: ({ id, datos }: { id: string; datos: unknown }) =>
      api.put<OperadorInfo>(`/plataforma/operadores/${id}`, datos),
    onSuccess: async () => cliente.invalidateQueries({ queryKey: ['plataforma', 'operadores'] }),
  });

  const restablecer = useMutation({
    mutationFn: (id: string) => api.post(`/plataforma/operadores/${id}/restablecer-mfa`),
    onSuccess: async () => cliente.invalidateQueries({ queryKey: ['plataforma', 'operadores'] }),
  });

  if (isLoading) return <Cargando />;

  const error = (crear.error ?? actualizar.error ?? restablecer.error) as ErrorApi | null;

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Operadores</h1>
          <p>Cuentas con acceso al padrón. Todas exigen segundo factor para poder ingresar.</p>
        </div>
        {puede('operador:administrar') ? (
          <button type="button" className="boton" onClick={() => setCreando((v) => !v)}>
            {creando ? 'Cancelar' : 'Nuevo operador'}
          </button>
        ) : null}
      </div>

      {error ? <Aviso tono="error">{error.message}</Aviso> : null}

      {creando ? (
        <FormularioOperador enviando={crear.isPending} onEnviar={(datos) => crear.mutate(datos)} />
      ) : null}

      <section className="panel">
        <div className="tabla-contenedor">
          <table className="tabla">
            <thead>
              <tr>
                <th>Operador</th>
                <th>Rol</th>
                <th>Segundo factor</th>
                <th>Última visita</th>
                <th>Estado</th>
                {puede('operador:administrar') ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {(operadores ?? []).map((o) => (
                <tr key={o.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>
                      {o.nombreCompleto}
                      {o.id === yo?.id ? ' (usted)' : ''}
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--texto-tenue)' }}>{o.email}</div>
                  </td>
                  <td>{ETIQUETA_ROL_OPERADOR[o.rol]}</td>
                  <td>
                    {o.mfaConfirmada ? (
                      <Etiqueta tono="verde">Inscrito</Etiqueta>
                    ) : (
                      <Etiqueta tono="ambar">Pendiente</Etiqueta>
                    )}
                  </td>
                  <td>{o.ultimaVisita ? formatearFechaHora(o.ultimaVisita) : 'Nunca ingresó'}</td>
                  <td>
                    {o.activo ? <Etiqueta tono="verde">Activo</Etiqueta> : <Etiqueta tono="gris">Inactivo</Etiqueta>}
                  </td>
                  {puede('operador:administrar') ? (
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="boton secundario pequeno"
                        disabled={restablecer.isPending || !o.mfaConfirmada}
                        title="Borra su segundo factor para que registre un teléfono nuevo"
                        onClick={() => restablecer.mutate(o.id)}
                      >
                        Restablecer MFA
                      </button>{' '}
                      <button
                        type="button"
                        className="boton secundario pequeno"
                        disabled={actualizar.isPending || o.id === yo?.id}
                        onClick={() => actualizar.mutate({ id: o.id, datos: { activo: !o.activo } })}
                      >
                        {o.activo ? 'Desactivar' : 'Activar'}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function FormularioOperador({
  enviando,
  onEnviar,
}: {
  enviando: boolean;
  onEnviar: (datos: unknown) => void;
}): JSX.Element {
  const [nombreCompleto, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [rol, setRol] = useState<RolOperador>('SOPORTE');
  const [contrasena, setContrasena] = useState('');

  return (
    <section className="panel" style={{ marginBottom: 16 }}>
      <div className="panel-cabecera">
        <h2>Nuevo operador</h2>
      </div>
      <form
        className="panel-cuerpo"
        onSubmit={(e) => {
          e.preventDefault();
          onEnviar({ nombreCompleto, email, rol, contrasena });
        }}
      >
        <Aviso tono="info">
          La cuenta nace sin segundo factor y no puede ingresar hasta que su dueño lo inscriba desde
          la pantalla de acceso. Usted nunca ve esa clave.
        </Aviso>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <div className="campo">
            <label htmlFor="op-nombre">Nombre completo</label>
            <input id="op-nombre" value={nombreCompleto} onChange={(e) => setNombre(e.target.value)} required />
          </div>
          <div className="campo">
            <label htmlFor="op-email">Correo</label>
            <input id="op-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="campo">
            <label htmlFor="op-rol">Rol</label>
            <select id="op-rol" value={rol} onChange={(e) => setRol(e.target.value as RolOperador)}>
              {ROLES_OPERADOR.map((r) => (
                <option key={r} value={r}>
                  {ETIQUETA_ROL_OPERADOR[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="op-contrasena">Contraseña inicial</label>
            <input
              id="op-contrasena"
              type="text"
              value={contrasena}
              onChange={(e) => setContrasena(e.target.value)}
              minLength={16}
              required
            />
          </div>
        </div>
        <p style={{ fontSize: 12, color: 'var(--texto-tenue)', margin: '4px 0 14px' }}>
          Mínimo 16 caracteres con mayúscula, minúscula, número y símbolo.
        </p>

        <button type="submit" className="boton" disabled={enviando}>
          {enviando ? 'Creando…' : 'Crear operador'}
        </button>
      </form>
    </section>
  );
}

/**
 * Bitácora de la consola.
 *
 * Registra quién cambió qué en el padrón, con IP y detalle. Es append-only en la
 * base: aquí solo se lee.
 */
export function Bitacora(): JSX.Element {
  const { data: eventos, isLoading } = useQuery({
    queryKey: ['plataforma', 'eventos'],
    queryFn: () => api.get<EventoOperadorInfo[]>('/plataforma/eventos?limite=200'),
  });

  if (isLoading) return <Cargando />;

  return (
    <>
      <div className="cabecera-pagina">
        <div>
          <h1>Bitácora de plataforma</h1>
          <p>Cada cambio sobre el padrón, con su autor. No se edita ni se borra.</p>
        </div>
      </div>

      {eventos && eventos.length > 0 ? (
        <section className="panel">
          <div className="tabla-contenedor">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Operador</th>
                  <th>Acción</th>
                  <th>Organización</th>
                  <th>Detalle</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {eventos.map((e) => (
                  <tr key={e.id}>
                    <td>{formatearFechaHora(e.fechaHora)}</td>
                    <td>{e.operadorEmail}</td>
                    <td>
                      <Etiqueta tono="azul">{e.accion}</Etiqueta>
                    </td>
                    <td>{e.tenantSlug ?? '—'}</td>
                    <td style={{ fontSize: 12, color: 'var(--texto-medio)' }}>
                      {Object.entries(e.detalle)
                        .map(([k, v]) => `${k}: ${String(v)}`)
                        .join(' · ') || '—'}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--texto-tenue)' }}>{e.ip ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : (
        <Vacio titulo="Sin actividad registrada" />
      )}
    </>
  );
}
